/**
 * WCC360 v2 — the on-device Career DNA™ snapshot.
 *
 * The sealed `learner_reports` payload is the authority, and it stays the
 * authority. But it can lag a finished run by minutes (or be blocked on a
 * Command Center change), and until it lands the rest of the shell — YoMA,
 * the Career DNA™ surface, the Explorer cards — had nothing at all to read
 * and quietly showed an empty state to a learner who had just answered
 * every question. That reads as "your assessment didn't count".
 *
 * So the result page writes the preview it already computed here, and the
 * shell falls back to it, clearly marked as a preview. This module stores
 * results — it computes nothing.
 */
import { bandParam, parseBand, type Band } from "./band";
import { cloudStorage } from "@/lib/cloudStore";

const KEY = "wcc360.v2.dna";

export interface LocalDnaMatch {
  id: string;
  name: string;
  cluster: string;
  tier: string | null;
  explanation: string | null;
}

export interface LocalDnaWorld {
  name: string;
  blurb: string;
}

export interface LocalDnaSnapshot {
  band: string;
  archetypePrimary: string | null;
  archetypeSecondary: string | null;
  headline: string | null;
  matches: LocalDnaMatch[];
  /** 12–14 only: the worlds the Explorer was shown, in the order shown. */
  worlds?: LocalDnaWorld[];
  savedAt: string;
}

export function writeLocalDna(band: Band, snap: Omit<LocalDnaSnapshot, "band" | "savedAt">) {
  try {
    cloudStorage.setItem(
      KEY,
      JSON.stringify({ ...snap, band: bandParam(band), savedAt: new Date().toISOString() }),
    );
  } catch { /* a private-mode browser is not a reason to break the result page */ }
}

export function readLocalDna(): LocalDnaSnapshot | null {
  try {
    const raw = cloudStorage.getItem(KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as LocalDnaSnapshot;
    if (!parsed || typeof parsed !== "object") return null;
    return {
      band: typeof parsed.band === "string" ? parsed.band : "18+",
      archetypePrimary: parsed.archetypePrimary ?? null,
      archetypeSecondary: parsed.archetypeSecondary ?? null,
      headline: parsed.headline ?? null,
      matches: Array.isArray(parsed.matches) ? parsed.matches : [],
      worlds: Array.isArray(parsed.worlds) ? parsed.worlds : undefined,
      savedAt: parsed.savedAt ?? new Date().toISOString(),
    };
  } catch { return null; }
}

export function localDnaBand(snap: LocalDnaSnapshot): Band {
  return parseBand(snap.band);
}

export function clearLocalDna() {
  try { cloudStorage.removeItem(KEY); } catch { /* ignore */ }
}
